/**
 * timetableRowModel — ScheduleDraft Timetable 行模型与编辑 payload 投影层。
 *
 * 本模块把 workspace Timetable rows 与原始 ScheduleDraftFlight 转换为
 * react-data-grid 可编辑行，并负责从编辑行还原写入值。航路和时间读取复用
 * lib 层工具，确保表格、弹窗和重算规则共享同一字段语义。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → lib/flightDataUtils.js (航路读取), lib/timeUtils.js (时间格式), components/editor/timetable/useTimetableGridEditing.js (提交消费)
 */

import { getFlightRouteBlockTime, getFlightRouteFullName, getFlightRouteName, getFlightRouteSummary, getRouteBlockTime } from "../../../lib/flightDataUtils";
import { getFlightBlockTimeMinutes, getFlightMinBlockTimeMinutes, getFlightMinGroundTimeMinutes, normalizeMinutes } from "../../../lib/flightTimingUtils";
import { buildDateTimeWithTime, formatBlockTime, formatTimeCell } from "../../../lib/timeUtils";

// 保持既有导入路径稳定，避免拆分 Timetable 时同步改动所有 cell adapter
export { buildDateTimeWithTime, formatBlockTime, formatTimeCell, getFlightRouteBlockTime, getFlightRouteFullName, getFlightRouteName, getFlightRouteSummary, getRouteBlockTime };

/**
 * 将 block time 分钟数格式化为 Timetable 详细文本。
 * @param {number|null|undefined} minutes - block time 分钟数。
 * @returns {string} 如 2h 35m 的展示文本。
 */
export function formatTimetableBlockTime(minutes) {
    if (!minutes) {
        return "--";
    }

    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * 将 workspace rows 转换为 react-data-grid 可编辑行。
 * @param {Array<Object>} rows - workspace Timetable 行。
 * @returns {Array<Object>} 可编辑 Grid 行。
 */
export function buildEditableRows(rows) {
    return rows.map((row) => {
        if (row._rowType === "rotation-separator") {
            return row;
        }

        return {
            ...row,
            _rowType: row._rowType || "flight",
            flightNumber: row.rawFlight?.flight_number || "",
            aircraftReg: row.rawFlight?.aircraft_reg || "",
            flightDate: row.rawFlight?.flight_date || "",
            departureAirport: row.rawFlight?.departure_airport || "",
            arrivalAirport: row.rawFlight?.arrival_airport || "",
            flightRouteId: row.rawFlight?.flight_route_id || null,
            flightRouteFullName: getFlightRouteFullName(row.rawFlight),
            flightRouteName: getFlightRouteName(row.rawFlight),
            flightRouteSummary: getFlightRouteSummary(row.rawFlight),
            flightRouteBlockTimeMinutes: getFlightRouteBlockTime(row.rawFlight),
            blockTimeMinutes: row.blockTimeMinutes ?? getFlightBlockTimeMinutes(row.rawFlight),
            minBlockTimeMinutes: row.minBlockTimeMinutes ?? getFlightMinBlockTimeMinutes(row.rawFlight),
            groundTimeMinutes: row.groundTimeMinutes ?? normalizeMinutes(row.rawFlight?.ground_time_minutes),
            minGroundTimeMinutes: row.minGroundTimeMinutes ?? getFlightMinGroundTimeMinutes(row.rawFlight),
            stdTime: formatTimeCell(row.rawFlight?.std),
            staTime: formatTimeCell(row.rawFlight?.sta),
            stcType: row.rawFlight?.stc_type || "",
            remarks: row.rawFlight?.remarks || "",
            isFixed: Boolean(row.rawFlight?.is_fixed),
        };
    });
}

/**
 * 将编辑后的 Grid 行还原为航班更新值。
 * @param {Object} row - react-data-grid 编辑行。
 * @returns {Object} ScheduleDraftFlight 更新值。
 */
export function buildFlightUpdateValues(row) {
    const rawFlight = row.rawFlight || {};
    const std = buildDateTimeWithTime(rawFlight.std, row.stdTime);
    const sta = buildDateTimeWithTime(rawFlight.sta || rawFlight.std, row.staTime);
    const blockTimeMinutes = row.blockTimeMinutes ?? row.flightRouteBlockTimeMinutes ?? getFlightBlockTimeMinutes(rawFlight);

    return {
        flight_number: row.flightNumber,
        aircraft_reg: row.aircraftReg,
        departure_airport: row.departureAirport,
        arrival_airport: row.arrivalAirport,
        std,
        sta,
        stc_type: row.stcType,
        is_fixed: Boolean(row.isFixed),
        remarks: row.remarks,
        flight_route_id: row.flightRouteId,
        block_time_minutes: blockTimeMinutes,
        min_block_time_minutes: row.minBlockTimeMinutes ?? getFlightMinBlockTimeMinutes(rawFlight),
    };
}

/**
 * 将航班对象映射为弹窗编辑表单初始值。
 * @param {Object|null} flight - ScheduleDraftFlight 对象。
 * @returns {Object} Antd Form 初始值。
 */
export function buildFlightFormValues(flight) {
    if (!flight) {
        return {};
    }

    return {
        flight_number: flight.flight_number,
        aircraft_reg: flight.aircraft_reg,
        departure_airport: flight.departure_airport,
        arrival_airport: flight.arrival_airport,
        std: flight.std,
        sta: flight.sta,
        stc_type: flight.stc_type,
        is_fixed: flight.is_fixed,
        remarks: flight.remarks,
        flight_route_id: flight.flight_route_id,
        block_time_minutes: getFlightBlockTimeMinutes(flight),
        min_block_time_minutes: getFlightMinBlockTimeMinutes(flight),
    };
}
